import { MenuItem } from 'primeng/api';

/**
 * Merges two arrays and removes duplicates.
 * @param array1 - The first array.
 * @param array2 - The second array.
 * @returns A new array with the elements of both arrays, without duplicates.
 */
export function mergeArrays<T>(array1: T[], array2: T[]): T[] {
  return Array.from(new Set([...array1, ...array2]));
}

/**
 * Makes a string array unique by removing duplicate entries.
 * Utilizes Set to filter out duplicate values efficiently.
 *
 * @param inputArray - The string array to be processed.
 * @returns A new array with unique strings.
 */
export function makeUniqueStringArray(inputArray: string[]): string[] {
  return Array.from(new Set(inputArray.map(item => String(item).trim())));
}

/**
 * Makes a number array unique by removing duplicate entries.
 * Utilizes Set to filter out duplicate values efficiently.
 *
 * @param inputArray - The string array to be processed.
 * @returns A new array with unique strings.
 */
export function makeUniqueNumberArray(inputArray: number[]): number[] {
  return Array.from(new Set(inputArray));
}

export function hasVisibleMenuItems(items: MenuItem[]): boolean {
  return !!items.filter((item: MenuItem) => {
    return item.visible;
  }).length;
}

export function checkVisibleMenuItems(items: MenuItem[]): MenuItem[] {
  return items.filter((item: MenuItem) => {
    return item.visible;
  }).length > 0
    ? items
    : [
        {
          label: 'keine Zugriffsrechte',
          icon: 'fas fa-info-circle',
        },
      ];
}

/**
 * Compares if two flat arrays of strings are identical in order and content.
 *
 * @param array1 - The first array to compare.
 * @param array2 - The second array to compare.
 * @returns True if the arrays are the same, otherwise false.
 */
export function areArraysIdentical(
  array1: string[],
  array2: string[]
): boolean {
  if (array1.length !== array2.length) return false;
  return array1.every((value, index) => value === array2[index]);
}

/**
 * Deep merge two objects
 * @param target The target object
 * @param source The source object to merge from
 * @returns A new object with deep merged values
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(result[key] as any, source[key] as any);
    } else {
      result[key] = source[key] as any;
    }
  }

  return result;
}

export function hasOverlap(arr1: string[], arr2: string[]): boolean {
  const set1 = new Set(arr1);
  return arr2.some(item => set1.has(item));
}

export function swap(array: any[]) {
  const b = array[0];
  array[0] = array[1];
  array[1] = b;
  return array;
}

/**
 * Returns a copy of the given items sorted alphabetically by their `name`,
 * using German collation (umlauts next to their base letter, case-insensitive).
 *
 * @param items - The items to sort; the input array is not mutated.
 * @returns A new, alphabetically sorted array.
 */
export function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    String(a.name ?? '').localeCompare(String(b.name ?? ''), 'de', {
      sensitivity: 'base',
    })
  );
}

/**
 * Returns `current` extended by all `items` which are not part of it yet (compared by `_id`).
 * Used to apply pick-list moves as a diff instead of replacing the whole selection.
 *
 * @param current - The current selection.
 * @param items - The items to add.
 * @returns A new array.
 */
export function addById<T extends { _id: string }>(
  current: T[],
  items: T[]
): T[] {
  const ids = new Set(current.map(item => item._id));
  const added = items.filter(item => {
    if (ids.has(item._id)) return false;
    ids.add(item._id);
    return true;
  });
  return [...current, ...added];
}

/**
 * Returns `current` without the items whose `_id` is contained in `ids`.
 *
 * @param current - The current selection.
 * @param ids - The ids to remove.
 * @returns A new array.
 */
export function removeByIds<T extends { _id: string }>(
  current: T[],
  ids: Iterable<string>
): T[] {
  const idSet = new Set(ids);
  return current.filter(item => !idSet.has(item._id));
}

/**
 * Checks whether two string arrays contain the same values, ignoring order and duplicates.
 *
 * @param a - The first array.
 * @param b - The second array.
 * @returns True if both contain the same set of values.
 */
export function haveSameValues(a: string[], b: string[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every(value => setB.has(value));
}
